import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useLocation } from 'react-router-dom';
import Header from '../../components/Header/Header';
import Footer from '../../components/Footer/Footer';
import ReadingText from '../BirthChart/ReadingText';
import { CARDS } from '../../data/cardMeanings';
import { useInterpreterCopy, interpreterEnglish } from '../../data/interpreterLanguage';
import { chartDirection, fill, useChartLanguage } from '../../data/chartLanguage';
import { LANGUAGE_ANSWER_OPTIONS } from '../../data/siteLanguages';
import { askInterpreter, interpretSpread } from '../../services/readingService';
import './Interpreter.css';

const SPREAD_IDS = ['one', 'three', 'four', 'five', 'celtic', 'custom'];
const SUIT_ORDER = ['major', 'wands', 'cups', 'swords', 'pentacles'];

const emptySlot = (position) => ({ key: `${position}-${Math.random().toString(36).slice(2, 8)}`, slug: '', reversed: false, position });

function slotsFor(spread, copy, previous = []) {
  if (spread === 'custom') {
    return previous.length ? previous : [emptySlot(copy.positionFallback.replace('{n}', '1'))];
  }
  const labels = copy.positions[spread];
  return labels.map((position, index) => ({
    key: previous[index]?.key || `${spread}-${index}`,
    slug: previous[index]?.slug || '',
    reversed: Boolean(previous[index]?.reversed),
    position,
  }));
}

function cardBySlug(slug) {
  return CARDS.find((card) => card.slug === slug) || null;
}

function displayName(card, lang) {
  if (!card) return '';
  return card.names[lang] || card.names.en;
}

function CardPicker({ id, card, taken, lang, copy, onChoose }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const boxRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const close = (event) => {
      if (!boxRef.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener('pointerdown', close);
    return () => document.removeEventListener('pointerdown', close);
  }, [open]);

  const groups = useMemo(() => {
    const q = query.trim().toLowerCase();
    return SUIT_ORDER.map((suit) => ({
      suit,
      label: copy.suits[suit],
      cards: CARDS.filter((item) => {
        if (item.arcana !== suit) return false;
        if (taken.has(item.slug) && item.slug !== card?.slug) return false;
        if (!q) return true;
        const names = [item.names.en, item.names[lang]].filter(Boolean).join(' ').toLowerCase();
        return names.includes(q);
      }),
    })).filter((group) => group.cards.length);
  }, [query, taken, card, lang, copy.suits]);

  return (
    <div className="interp-picker" ref={boxRef}>
      <button
        type="button"
        className="interp-picker-button"
        aria-expanded={open}
        aria-controls={`${id}-list`}
        onClick={() => setOpen((value) => !value)}
      >
        {card ? displayName(card, lang) : copy.chooseCard}
      </button>
      {open && (
        <div className="interp-picker-panel" id={`${id}-list`}>
          <input
            type="search"
            value={query}
            placeholder={copy.searchPlaceholder}
            aria-label={copy.searchPlaceholder}
            onChange={(event) => setQuery(event.target.value)}
            autoFocus
          />
          <div className="interp-picker-list" role="listbox" aria-label={copy.chooseCard}>
            {groups.length === 0 && <p className="interp-empty">{copy.noMatches}</p>}
            {groups.map((group) => (
              <div key={group.suit}>
                <p className="interp-suit">{group.label}</p>
                {group.cards.map((item) => (
                  <button
                    type="button"
                    key={item.slug}
                    role="option"
                    aria-selected={item.slug === card?.slug}
                    onClick={() => {
                      onChoose(item.slug);
                      setOpen(false);
                      setQuery('');
                    }}
                  >
                    {displayName(item, lang)}
                  </button>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const Interpreter = () => {
  const location = useLocation();
  const { language, chooseLanguage } = useChartLanguage();
  const copy = useInterpreterCopy(language);
  const [spread, setSpread] = useState('three');
  const [slots, setSlots] = useState(() => slotsFor('three', interpreterEnglish));
  const [question, setQuestion] = useState('');
  const [phase, setPhase] = useState('form');
  const [reading, setReading] = useState('');
  const [session, setSession] = useState('');
  const [error, setError] = useState('');
  const [followup, setFollowup] = useState('');
  const [answers, setAnswers] = useState([]);
  const [asking, setAsking] = useState(false);
  const resultRef = useRef(null);
  const appliedCard = useRef(false);
  const spreadRef = useRef(spread);
  spreadRef.current = spread;

  useEffect(() => {
    setSlots((current) => slotsFor(spreadRef.current, copy, current));
  }, [copy]);

  useEffect(() => {
    if (appliedCard.current) return;
    if (typeof navigator !== 'undefined' && navigator.userAgent === 'ReactSnap') return;
    const slug = new URLSearchParams(location.search).get('card');
    if (!slug || !cardBySlug(slug)) return;
    appliedCard.current = true;
    setSlots((current) => current.map((slot, index) => (
      index === 0 ? { ...slot, slug } : slot
    )));
  }, [location.search]);

  const taken = useMemo(() => new Set(slots.map((slot) => slot.slug).filter(Boolean)), [slots]);
  const ready = slots.length > 0 && slots.every((slot) => slot.slug);

  const chooseSpread = (next) => {
    setSpread(next);
    setSlots((current) => slotsFor(next, copy, next === 'custom' ? [] : current));
    setError('');
  };

  const updateSlot = (key, patch) => {
    setSlots((current) => current.map((slot) => (slot.key === key ? { ...slot, ...patch } : slot)));
  };

  const addSlot = () => {
    if (slots.length >= 15) return;
    setSlots((current) => [
      ...current,
      emptySlot(fill(copy.positionFallback, { n: current.length + 1 })),
    ]);
  };

  const moveSlot = (index, direction) => {
    setSlots((current) => {
      const next = current.slice();
      const target = index + direction;
      if (target < 0 || target >= next.length) return current;
      const [item] = next.splice(index, 1);
      next.splice(target, 0, item);
      return next;
    });
  };

  const submit = async (event) => {
    event.preventDefault();
    if (phase === 'reading') return;
    if (!ready) {
      setError(copy.needCards);
      return;
    }
    setError('');
    setReading('');
    setAnswers([]);
    setSession('');
    setPhase('reading');
    const cards = slots.map((slot, index) => {
      const card = cardBySlug(slot.slug);
      const position = spread === 'custom'
        ? (slot.position.trim() || fill(copy.positionFallback, { n: index + 1 }))
        : copy.positions[spread][index];
      return { name: card.names.en, reversed: slot.reversed, position };
    });
    await interpretSpread(
      { spread, question: question.trim(), cards, language },
      {
        onToken: (token) => setReading((current) => current + token),
        onDone: (data) => {
          setPhase('done');
          if (data?.session) setSession(data.session);
          setTimeout(() => resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 30);
        },
        onError: (message) => {
          setPhase('form');
          setError(message || copy.error);
        },
      },
    );
  };

  const ask = async (text) => {
    const next = (text || followup).trim();
    if (!next || !session || asking) return;
    setAsking(true);
    setError('');
    setFollowup('');
    let answer = '';
    setAnswers((current) => [...current, { question: next, answer: '' }]);
    await askInterpreter(session, next, {
      onToken: (token) => {
        answer += token;
        setAnswers((current) => current.map((item, index) => (
          index === current.length - 1 ? { ...item, answer } : item
        )));
      },
      onDone: () => setAsking(false),
      onError: (message) => {
        setAsking(false);
        setError(message || copy.error);
      },
    });
  };

  const faq = interpreterEnglish.faq;
  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebApplication',
        name: 'Free AI Tarot Card Interpreter',
        url: 'https://nowtarot.com/tarot-card-interpreter/',
        applicationCategory: 'LifestyleApplication',
        offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
        description: 'Enter tarot cards you already drew and get a free AI interpretation of the whole spread.',
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://nowtarot.com/' },
          { '@type': 'ListItem', position: 2, name: 'Free AI Tarot Card Interpreter', item: 'https://nowtarot.com/tarot-card-interpreter/' },
        ],
      },
      {
        '@type': 'FAQPage',
        mainEntity: faq.map((item) => ({
          '@type': 'Question',
          name: item.q,
          acceptedAnswer: { '@type': 'Answer', text: item.a },
        })),
      },
    ],
  };

  return (
    <div className="interp-page">
      <Helmet>
        <title>Free AI Tarot Card Interpreter – Read Your Own Cards | NowTarot</title>
        <meta name="description" content="Pulled your own tarot cards? Enter your physical cards, choose a spread, and get a free AI interpretation. Explore upright, reversed, and combined meanings." />
        <link rel="canonical" href="https://nowtarot.com/tarot-card-interpreter/" />
        <meta property="og:title" content="Free AI Tarot Card Interpreter – Read Your Own Cards | NowTarot" />
        <meta property="og:description" content="Pulled your own tarot cards? Enter your physical cards, choose a spread, and get a free AI interpretation. Explore upright, reversed, and combined meanings." />
        <meta property="og:url" content="https://nowtarot.com/tarot-card-interpreter/" />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>
      <Header />
      <main {...chartDirection(language)}>
        <p className="interp-eyebrow">{copy.eyebrow}</p>
        <h1>{copy.title}</h1>
        <p className="interp-lede">{copy.lede}</p>

        <form className="interp-form" onSubmit={submit}>
          <label className="interp-question">
            <span>{copy.questionLabel} <em>{copy.questionOptional}</em></span>
            <input
              value={question}
              maxLength={500}
              placeholder={copy.questionPlaceholder}
              onChange={(event) => setQuestion(event.target.value)}
            />
          </label>

          <fieldset className="interp-spreads">
            <legend>{copy.spreadLabel}</legend>
            <div>
              {SPREAD_IDS.map((id) => (
                <label key={id} className={spread === id ? 'is-on' : ''}>
                  <input
                    type="radio"
                    name="spread"
                    value={id}
                    checked={spread === id}
                    onChange={() => chooseSpread(id)}
                  />
                  {copy.spreads[id]}
                </label>
              ))}
            </div>
          </fieldset>

          <ol className={`interp-board interp-board-${spread}`}>
            {slots.map((slot, index) => {
              const card = cardBySlug(slot.slug);
              const label = spread === 'custom'
                ? fill(copy.positionFallback, { n: index + 1 })
                : copy.positions[spread][index];
              return (
                <li key={slot.key} className={`interp-slot interp-slot-${index}`}>
                  <div className="interp-slot-head">
                    <span>{index + 1}</span>
                    {spread === 'custom' ? (
                      <input
                        aria-label={copy.positionName}
                        placeholder={copy.positionName}
                        value={slot.position}
                        maxLength={60}
                        onChange={(event) => updateSlot(slot.key, { position: event.target.value })}
                      />
                    ) : <strong>{label}</strong>}
                  </div>
                  <div className="interp-slot-body">
                    {card ? (
                      <img src={card.image} alt="" width="90" height="158" />
                    ) : (
                      <span className="interp-blank" aria-hidden="true" />
                    )}
                    <div>
                      <CardPicker
                        id={`card-${slot.key}`}
                        card={card}
                        taken={taken}
                        lang={language}
                        copy={copy}
                        onChoose={(slug) => updateSlot(slot.key, { slug })}
                      />
                      <div className="interp-orient" role="group" aria-label={label}>
                        <button
                          type="button"
                          className={!slot.reversed ? 'is-on' : ''}
                          aria-pressed={!slot.reversed}
                          onClick={() => updateSlot(slot.key, { reversed: false })}
                        >
                          {copy.upright}
                        </button>
                        <button
                          type="button"
                          className={slot.reversed ? 'is-on' : ''}
                          aria-pressed={slot.reversed}
                          onClick={() => updateSlot(slot.key, { reversed: true })}
                        >
                          {copy.reversed}
                        </button>
                      </div>
                    </div>
                  </div>
                  {spread === 'custom' && (
                    <div className="interp-slot-actions">
                      <button type="button" onClick={() => moveSlot(index, -1)} disabled={index === 0}>{copy.moveUp}</button>
                      <button type="button" onClick={() => moveSlot(index, 1)} disabled={index === slots.length - 1}>{copy.moveDown}</button>
                      <button
                        type="button"
                        onClick={() => setSlots((current) => (
                          current.length === 1
                            ? [{ ...current[0], slug: '' }]
                            : current.filter((item) => item.key !== slot.key)
                        ))}
                      >
                        {copy.remove}
                      </button>
                    </div>
                  )}
                  {spread !== 'custom' && slot.slug && (
                    <button type="button" className="interp-clear" onClick={() => updateSlot(slot.key, { slug: '' })}>
                      {copy.remove}
                    </button>
                  )}
                </li>
              );
            })}
          </ol>

          {spread === 'custom' && slots.length < 15 && (
            <button type="button" className="interp-add" onClick={addSlot}>{copy.addCard}</button>
          )}

          <label className="interp-language">
            <span>{copy.languageLabel}</span>
            <select value={language} onChange={(event) => chooseLanguage(event.target.value)}>
              {LANGUAGE_ANSWER_OPTIONS.filter((option) => option.value).map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </label>

          {error && <p className="interp-error" role="alert">{error}</p>}
          <button className="interp-go" type="submit" disabled={!ready || phase === 'reading'}>
            {phase === 'reading' ? copy.interpreting : copy.interpret}
          </button>
          <p className="interp-aside">
            {copy.noDeck} <Link to="/free-tarot/">{copy.noDeckCta}</Link>
          </p>
        </form>

        {(phase !== 'form' || reading) && (
          <section className="interp-result" ref={resultRef} aria-live="polite">
            {phase === 'reading' && !reading && <p className="interp-wait">{copy.interpreting}</p>}
            {reading && <ReadingText text={reading} lang={language} dir={language === 'ar' ? 'rtl' : undefined} />}
            {session && (
              <div className="interp-ask">
                <h2>{copy.askTitle}</h2>
                <p>{copy.askText}</p>
                <div className="interp-examples">
                  <p>{copy.examplesLabel}</p>
                  <div>
                    {copy.examples.map((item) => (
                      <button type="button" key={item} onClick={() => ask(item)} disabled={asking}>{item}</button>
                    ))}
                  </div>
                </div>
                {answers.map((item) => (
                  <article key={item.question}>
                    <h3>{item.question}</h3>
                    <ReadingText text={item.answer} lang={language} dir={language === 'ar' ? 'rtl' : undefined} />
                  </article>
                ))}
                <form
                  onSubmit={(event) => {
                    event.preventDefault();
                    ask();
                  }}
                >
                  <label>
                    <span className="interp-sr">{copy.askPlaceholder}</span>
                    <input
                      value={followup}
                      maxLength={500}
                      placeholder={copy.askPlaceholder}
                      onChange={(event) => setFollowup(event.target.value)}
                    />
                  </label>
                  <button type="submit" disabled={asking || !followup.trim()}>
                    {asking ? copy.asking : copy.askButton}
                  </button>
                </form>
              </div>
            )}
            {phase === 'done' && (
              <button type="button" className="interp-edit" onClick={() => resultRef.current?.previousElementSibling?.scrollIntoView({ behavior: 'smooth' })}>
                {copy.edit}
              </button>
            )}
          </section>
        )}

        <section className="interp-about">
          <h2>{copy.aboutTitle}</h2>
          <div className="interp-about-grid">
            {copy.about.map((item) => (
              <article key={item.title}>
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="interp-faq">
          <h2>{copy.faqTitle}</h2>
          {copy.faq.map((item) => (
            <article key={item.q}>
              <h3>{item.q}</h3>
              <p>{item.a}</p>
            </article>
          ))}
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Interpreter;
